/**
 * In-widget memory editor. Web replacement for the native "open
 * memory.md in the default text editor" affordance — the web widget
 * can't launch a native editor, so we render a pixel-dialogue-box
 * modal with a plain textarea and a Save button.
 *
 * The textarea's contents are exactly what `KokoMemoryStore` persists
 * to localStorage, so the user can read, prune, or hand-author memory
 * lines.
 */

import type { KokoMemoryStore } from "./KokoMemoryStore";

export interface MemoryEditorOptions {
  store: KokoMemoryStore;
}

export class MemoryEditor {
  private readonly rootElement: HTMLDivElement;
  private readonly textarea: HTMLTextAreaElement;
  private readonly options: MemoryEditorOptions;
  private keyDownListener: ((event: KeyboardEvent) => void) | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  constructor(options: MemoryEditorOptions) {
    this.options = options;
    this.rootElement = document.createElement("div");
    this.rootElement.className = "koko-pixel-box koko-memory-editor";

    const fillLayer = document.createElement("div");
    fillLayer.className = "koko-pixel-box-fill";

    const header = document.createElement("div");
    header.className = "koko-text-response-header";
    const label = document.createElement("span");
    label.className = "koko-label";
    label.textContent = "MEMORY";
    const hint = document.createElement("span");
    hint.className = "koko-esc-hint";
    hint.textContent = "esc";
    header.appendChild(label);
    header.appendChild(hint);

    this.textarea = document.createElement("textarea");
    this.textarea.className = "koko-memory-textarea";
    this.textarea.setAttribute("spellcheck", "false");
    this.textarea.setAttribute("autocomplete", "off");

    const toolbar = document.createElement("div");
    toolbar.className = "koko-memory-toolbar";

    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className = "koko-memory-save";
    saveButton.textContent = "SAVE";
    saveButton.addEventListener("click", () => {
      this.save();
      this.hide();
    });

    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = "koko-memory-clear";
    clearButton.textContent = "CLEAR";
    clearButton.addEventListener("click", () => {
      this.options.store.clear();
      this.textarea.value = this.options.store.readRawForEditor();
    });

    toolbar.appendChild(clearButton);
    toolbar.appendChild(saveButton);

    fillLayer.appendChild(header);
    fillLayer.appendChild(this.textarea);
    fillLayer.appendChild(toolbar);
    this.rootElement.appendChild(fillLayer);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.rootElement);
  }

  show(): void {
    this.textarea.value = this.options.store.readRawForEditor();
    this.positionCentered();
    // Put the editor above every other shadow-root surface so its
    // click target isn't intercepted by a panel row behind it.
    this.rootElement.style.zIndex = "2147483647";
    this.rootElement.classList.add("visible");
    this.installDismissListeners();
    window.setTimeout(() => this.textarea.focus(), 0);
  }

  hide(): void {
    this.rootElement.classList.remove("visible");
    this.removeDismissListeners();
  }

  isVisible(): boolean {
    return this.rootElement.classList.contains("visible");
  }

  destroy(): void {
    this.hide();
    this.rootElement.remove();
  }

  private save(): void {
    this.options.store.writeRaw(this.textarea.value);
  }

  private positionCentered(): void {
    // Centered modal — unlike the response/input bubbles the editor
    // is a deliberate focus-stealing UI, so it pops in the middle of
    // the viewport regardless of the bird's current position.
    const panelWidth = 520;
    const panelHeight = 440;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    this.rootElement.style.width = `${panelWidth}px`;
    this.rootElement.style.height = `${panelHeight}px`;
    this.rootElement.style.left = `${Math.max(8, (viewportWidth - panelWidth) / 2)}px`;
    this.rootElement.style.top = `${Math.max(8, (viewportHeight - panelHeight) / 2)}px`;
  }

  private installDismissListeners(): void {
    if (this.keyDownListener || this.outsideClickListener) return;
    this.keyDownListener = (event) => {
      if (event.key === "Escape") this.hide();
    };
    this.outsideClickListener = (event) => {
      const path = event.composedPath();
      if (!path.includes(this.rootElement)) {
        this.hide();
      }
    };
    window.setTimeout(() => {
      if (this.keyDownListener) {
        document.addEventListener("keydown", this.keyDownListener);
      }
      if (this.outsideClickListener) {
        document.addEventListener("mousedown", this.outsideClickListener, true);
      }
    }, 0);
  }

  private removeDismissListeners(): void {
    if (this.keyDownListener) {
      document.removeEventListener("keydown", this.keyDownListener);
      this.keyDownListener = null;
    }
    if (this.outsideClickListener) {
      document.removeEventListener("mousedown", this.outsideClickListener, true);
      this.outsideClickListener = null;
    }
  }
}
